/**
 * SideNav.tsx — the full-height staff sidebar RAIL + the staff shell layout.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 5 (app-shell
 * redesign, 2026-07-21 — evolves the Wave-3a floating-card sidebar); polished
 * to analytics-panel parity in UX Wave 7B (2026-07-21 late).
 *
 * WHY: the CTO's Wave-5 shell feedback replaces the floating-card sidebar
 * with an Agency-Accelerator-style edge-to-edge RAIL: full viewport height,
 * owning the left edge. Wave 7B re-orders the rail to match the analytics
 * admin webapp: the WAFFLE clinic switcher sits at the very top (the logo
 * moved OFF the green rail onto the white masthead block on the top bar —
 * see SideNavLayout's `masthead` slot), an uppercase "Menu" section label
 * introduces the items (analytics look/feel), the item glyphs are
 * outline-only inline SVGs (ui/icons.tsx — stroke `currentColor`, the emoji
 * are gone), and the rail foot carries the collapse control. ONE shared
 * component for the staff consoles AND the patient portal (2026-07-25 — the
 * patient area now mounts this same rail; verify role-conditional items, never
 * forked code — the Wave-3 rule stands): the owner passes its items and holds
 * the active key, so the
 * primitive stays state-free about WHICH section is open and the existing
 * `?tab=` URL-sync (same keys, deep-link compat) lives untouched in the
 * pages. The `brand` and `switcher` slots keep this primitive
 * router/roster-agnostic (Wave 7B: the consoles no longer pass `brand` — the
 * logo lives on the masthead — but the slot stays for any future rail-top
 * chrome).
 *
 * SEMANTICS: this is site-section NAVIGATION, not a WAI-ARIA tab widget — a
 * `<nav>` landmark (labelled per console) over a list of buttons, the active
 * item marked `aria-current="page"` (each item maps to a shareable `?tab=`
 * URL). Buttons rather than links so the pages keep their existing
 * URLSearchParams handling (`?clinic=` and friends survive a section switch).
 *
 * COLLAPSIBLE: the rail collapses to an ICON-ONLY strip (Wave 5 — the items
 * never leave the DOM; their labels drop to `gps-sr-only` so every button
 * keeps its accessible name and `title` tooltip). On narrower desktop widths
 * (≤1023px — the staff consoles are DesktopOnly ≥768px; that rule is
 * unchanged) it starts collapsed; the toggle (aria-expanded) flips it at any
 * width. jsdom has no matchMedia, so tests render expanded by default —
 * mirroring DesktopOnly's approach.
 *
 * FIXED-RAIL MECHANICS: `position: sticky; top: 0; height: 100vh` inside the
 * shell's flex row — the rail stays put while the working pane scrolls, with
 * no margin-syncing a `position: fixed` rail would need.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { IconCollapse, IconExpand } from './icons';

export interface SideNavItem<K extends string = string> {
  /** Stable key — the `?tab=` URL key, NOT the visible label. */
  key: K;
  label: string;
  /** The leading glyph (decorative — the label carries the accessible name). */
  icon?: ReactNode;
}

const NARROW_QUERY = '(max-width: 1023px)';

function matchesNarrow(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(NARROW_QUERY).matches;
}

/** The rail's deep-green ground — the same brand gradient the patient header wears. */
const RAIL_BACKGROUND = 'linear-gradient(180deg, #0f3d2c 0%, #0b2e21 100%)';

export function SideNav<K extends string>({
  label,
  items,
  activeKey,
  onSelect,
  brand,
  switcher,
}: {
  label: string;
  items: ReadonlyArray<SideNavItem<K>>;
  activeKey: K;
  onSelect: (key: K) => void;
  /** Optional rail-top slot ABOVE the switcher (unused by the consoles since
   * Wave 7B moved the logo to the masthead; kept generic). */
  brand?: ReactNode;
  /** The waffle clinic-switcher slot — the TOP of the rail (Wave 7B §4). */
  switcher?: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(matchesNarrow);

  // Track viewport crossings live: dropping under the narrow width collapses
  // the rail, growing back expands it (a manual toggle wins until the next
  // crossing — the same pragmatic trade DesktopOnly makes).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(NARROW_QUERY);
    const onChange = () => setCollapsed(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return (
    <nav
      aria-label={label}
      className="gps-staff-rail"
      data-collapsed={collapsed}
      style={{
        flex: 'none',
        width: collapsed ? '68px' : '236px',
        // Full-height and pinned: the rail owns the viewport's left edge and
        // stays put while the working pane scrolls (Wave 5 §1).
        position: 'sticky',
        top: 0,
        height: '100vh',
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gps-s-3)',
        background: RAIL_BACKGROUND,
        padding: 'var(--gps-s-3) var(--gps-s-2)',
        boxSizing: 'border-box',
        transition: 'width var(--gps-dur) var(--gps-ease)',
      }}
    >
      {brand && !collapsed ? <div style={{ padding: '2px 6px' }}>{brand}</div> : null}

      {/* Wave 7B §4: the WAFFLE clinic switcher leads the rail — the logo
          moved to the white masthead block on the top bar. */}
      {switcher ?? null}

      {items.length > 0 ? (
        <>
          {/* The analytics-style uppercase section label over the menu
              (decorative — the nav landmark itself carries the real name). */}
          {!collapsed ? (
            <div
              aria-hidden="true"
              style={{
                margin: 'var(--gps-s-1) var(--gps-s-2) 0',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'rgba(255, 255, 255, 0.55)',
              }}
            >
              Menu
            </div>
          ) : null}
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            {items.map(({ key, label: itemLabel, icon }) => {
              const active = key === activeKey;
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="gps-rail-link"
                    aria-current={active ? 'page' : undefined}
                    title={collapsed ? itemLabel : undefined}
                    onClick={() => onSelect(key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      gap: '12px',
                      width: '100%',
                      textAlign: 'left',
                      minHeight: '44px',
                      padding: collapsed ? '10px 0' : '10px 12px',
                      background: active ? 'rgba(255, 255, 255, 0.16)' : 'none',
                      border: 0,
                      // Active is never colour-alone: the accent edge + weight +
                      // aria-current carry it alongside the tint.
                      borderLeft: `3px solid ${active ? 'var(--gps-on-accent)' : 'transparent'}`,
                      borderRadius: 'var(--gps-r-sm)',
                      color: active ? 'var(--gps-on-accent)' : 'rgba(255, 255, 255, 0.82)',
                      font: 'inherit',
                      // Analytics menu density (Wave 7B §6): medium weight at
                      // rest, semibold only for the active row.
                      fontSize: '14.5px',
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: '20px',
                        flex: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '15px',
                      }}
                    >
                      {icon ?? '•'}
                    </span>
                    {/* Collapsed = icon-only: the label leaves the eye but NEVER
                        the accessibility tree (Wave 5 — sr-only, not unmounted). */}
                    <span className={collapsed ? 'gps-sr-only' : undefined}>{itemLabel}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {/* Rail FOOT: a hairline rule + the collapse/expand control as a full
          nav-style row. */}
      <div
        style={{
          marginTop: 'auto',
          borderTop: '1px solid rgba(255, 255, 255, 0.18)',
          paddingTop: 'var(--gps-s-2)',
        }}
      >
        <button
          type="button"
          className="gps-rail-link"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
          onClick={() => setCollapsed((value) => !value)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: '12px',
            width: '100%',
            textAlign: 'left',
            minHeight: '44px',
            padding: collapsed ? '10px 0' : '10px 12px',
            border: 0,
            borderLeft: '3px solid transparent',
            borderRadius: 'var(--gps-r-sm)',
            background: 'none',
            color: 'rgba(255, 255, 255, 0.82)',
            font: 'inherit',
            fontSize: '14.5px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{ width: '20px', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {collapsed ? <IconExpand /> : <IconCollapse />}
          </span>
          {!collapsed ? <span aria-hidden="true">Collapse</span> : null}
        </button>
      </div>
    </nav>
  );
}

/**
 * The staff shell layout that pairs with {@link SideNav}: the full-height
 * rail on the left, then a column of [top bar → full-width working pane].
 * The top bar is the Wave-7B WHITE HEADER BLOCK: the `masthead` slot (left)
 * carries the GPS logo + workspace name + the per-section breadcrumb (see
 * ui/StaffMasthead.tsx — the analytics logo-plus-product pattern; the old
 * in-content page-title block is gone), and the `account` slot (right) keeps
 * the shared ProfileMenu. The working content is CAPPED + centred at
 * `CONTENT_MAX` (1600px, 2026-07-25) with generous gutters, and the full-bleed
 * top bar caps its INNER content to the same width so the masthead + account
 * line up with the pane on ultra-wide displays (superseding the earlier Wave 5
 * §4 fluid-`<main>` model). `minWidth: 0` lets wide section content (the board's
 * internal scroller, data tables) shrink inside the flex row instead of
 * blowing the page out sideways.
 *
 * FROZEN TOP BAR (Wave 6 §2): the compact bar is `position: sticky; top: 0`
 * inside its column, so it stays pinned while the working pane scrolls on
 * BOTH consoles — mirroring the rail's own sticky mechanics. Its z-index sits
 * above plain content but BELOW the kit's transient layers (Menu dropdowns at
 * 30, Drawer/ConfirmDialog overlays at 1000), so nothing interactive is ever
 * trapped behind it.
 */
export function SideNavLayout({
  nav,
  masthead,
  account,
  children,
}: {
  nav: ReactNode;
  /** The white header block's LEFT side: logo + workspace name + breadcrumb
   * (Wave 7B — the pages pass the shared StaffMasthead). */
  masthead?: ReactNode;
  /** The top-bar account control (avatar/initials menu) — right-aligned. */
  account?: ReactNode;
  children: ReactNode;
}) {
  // The working content is capped + centred on large screens (2026-07-25) and
  // gets a touch more L/R breathing room than the raw token gutter; the frozen
  // top bar is full-bleed but its INNER content is capped to the SAME width so
  // the masthead + account line up with the pane below on ultra-wide displays.
  const CONTENT_MAX = '1600px';
  const CONTENT_PX = 'clamp(24px, 3vw, 40px)';
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '100vh', background: 'var(--gps-bg)' }}>
      {nav}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {account || masthead ? (
          <header
            className="gps-staff-topbar"
            style={{
              minHeight: '56px',
              background: 'var(--gps-surface)',
              borderBottom: '1px solid var(--gps-border)',
              boxSizing: 'border-box',
              // Wave 6 §2: FROZEN — the bar stays pinned while the working
              // pane scrolls (see the SideNavLayout doc block for the z-order
              // contract with menus and drawers).
              position: 'sticky',
              top: 0,
              zIndex: 20,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: masthead ? 'space-between' : 'flex-end',
                alignItems: 'center',
                gap: 'var(--gps-s-3)',
                width: '100%',
                maxWidth: CONTENT_MAX,
                margin: '0 auto',
                minHeight: '56px',
                padding: `8px ${CONTENT_PX}`,
                boxSizing: 'border-box',
              }}
            >
              {masthead ?? null}
              {account ?? null}
            </div>
          </header>
        ) : null}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            maxWidth: CONTENT_MAX,
            margin: '0 auto',
            padding: `var(--gps-s-5) ${CONTENT_PX} var(--gps-s-6)`,
            boxSizing: 'border-box',
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
